/**
 * What a plant is: Plant, then Type, then Variety, as standard drop-downs.
 *
 * The same three fields wherever a plant is named — adding one, editing one,
 * filing photos as a new one — in the same order. Each only appears when it
 * has a real choice to offer: Type when a crop has more than one, Variety
 * when a type has named varieties, and Variety starts on "Not sure", so it
 * never has to be answered. Changing the plant clears Type and Variety;
 * changing the type puts Variety back to "Not sure". Nothing else moves.
 *
 * Type has a "Not sure" option too (B52), first under "Choose a type", for a gardener who
 * does not know which type it is: the choice then carries the crop and typeUnsure and no
 * type, Variety is not drawn (a variety belongs to a type), and the server files the plant
 * on the crop's usual type. Type itself still starts empty and has to be answered, "Not
 * sure" being one answer. A crop with one type has no Type list and so no such option.
 *
 * It replaces a full-screen chooser with chips, counts, a garden list and a
 * second step, which every gardener had to learn before they could answer.
 *
 * The photo tray asks for the plant in its own list, one that also holds
 * the plants already growing there, and uses these for Type and Variety
 * only (showPlant false).
 *
 * A variety Grow does not have can be named (2026-10-03): "Add your own
 * variety…" at the foot of the Variety list, or, for a type with no named
 * varieties, a "+ Add a variety" link under it. What is typed becomes the
 * gardener's own variety of that type (or the one already there by that
 * name) and is chosen; their own are marked "(yours)" in the list.
 *
 * The search (2026-10-05) lists what it finds under its box: crops, types
 * ("Romaine · Lettuce") and varieties, Grow's and the gardener's own
 * ("Sun Gold · Cherry"). Choosing one sets Plant, Type and
 * Variety at once and empties the box. It still narrows the Plant
 * drop-down while something is typed. Since B121 the server decides it all
 * (usePlantSearch, GET /plants/search): which rows answer, their names and
 * details, their order and how many, and which crops the drop-down keeps;
 * the fields draw the answer as sent, and a failure says the standard
 * sentence under the box (ServerFailureNote, reported to Sentry). A search that finds nothing says so,
 * and once a plant is chosen offers to add what was typed as the
 * gardener's own variety of it. It no longer chooses a lone match by
 * itself as the gardener types: the list shows it, and Enter takes it.
 *
 * The photo tray can offer a variety Grow saw that is not one of the type's
 * named ones (seen, 2026-10-07): "Farao F1 (from the tag)" after "Not sure"
 * in the Variety list, chosen while it is what will be filed, so the list
 * and the tray's button always agree. "Not sure" then files no variety.
 *
 * A search that finds nothing also offers "Not in Grow's list? Describe it"
 * (DescribePlant, 2026-10-10): Grow says what the plant is and the closest
 * plant it has ("Grow thinks this is bee balm (Monarda), a perennial flower
 * in the mint family. File it under Perennial flower?"), and Yes files it
 * as the gardener's own and chooses it. The search also finds a crop by its
 * other names ("coriander", "courgette", "Monarda"; the server's search).
 *
 * The lists are the server's (W24 Job B; the owner's rule 1): the Plant list is the crops answer's groups as sent (GET
 * /plants/picker/crops, "What you grow" first: the server's `mine`), the Type list one crop's types answer (GET
 * /plants/picker/types, asked when a crop is chosen: its order, its labels, "(yours)" on her own, "Not sure" first when
 * it offers one), and the Variety list one type's varieties answer (GET /plants/picker/varieties, asked when a type is
 * chosen: "Not sure" first, then its order). Nothing is sorted, labelled or started here: a crop or type picked in these
 * lists starts where the server's `start` says, applied when its answer arrives (usePickStarts), never over a type or
 * "Not sure" already chosen for that crop. The fields no longer read the garden's plant list (so an open form on Today
 * pulls no plant list either).
 *
 * A variety of their own that could not be added says why under its box:
 * the server's refusal, or for a failure on our side (D31,
 * lib/serverFailure) the standard sentence, reported to Sentry, with the
 * support address as a link.
 *
 * @module PlantIdentityFields
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { Input } from '@/shared/components/ui/input';
import { NoPlantFound, PlantSearchField } from './PlantSearchField';
import { DescribePlant } from './DescribePlant';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { addOwnVariety } from '@/shared/api/plantCatalogue';
import type { PickerCrop, PickerVarieties } from '@/shared/api/plantPicker';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';
import { PLANT_SEARCH_QUERY_KEY, usePlantSearch } from './usePlantSearch';
import {
  pickerKeys, pickerStale, useCropTypes, usePickStarts, usePickerCrops, usePickerScope, useTypeVarieties, type CropChoice,
} from './usePlantPicker';
import { useGrowLimits } from '@/shared/lib/growSettings';

/** The Variety list's last option, which opens the box to name one. */
const ADD_OWN = '__add';

/** The Type list's "Not sure" option (B52): no type, the crop and the flag. */
const TYPE_UNSURE = '__unsure';

/** The Variety list's option for a variety Grow saw (seen), filed by its name. */
const SEEN = '__seen';

/**
 * A variety Grow saw in a photo that is not one of the type's named ones (a
 * plant tag's "Farao F1", a flower's "Zinnia"): offered in the Variety list
 * by its label, and filed by its name while it is chosen.
 */
export interface SeenVarietyOption {
  /** What the list shows: "Farao F1 (from the tag)". */
  label: string;
  /** It is the one chosen: the list shows it, and it is what will be filed. */
  chosen: boolean;
  /** Called with true when it is chosen, and false when anything else in the Variety list is. */
  onChoose: (chosen: boolean) => void;
}

/** What the fields are given: the choice so far, and how to show it. */
interface PlantIdentityFieldsProps {
  /** The crop, type and variety chosen so far. */
  value: CropChoice;
  /** Called with the whole choice after any field changes. */
  onChange: (next: CropChoice) => void;
  /** Which field to mark as still needing an answer. */
  missing?: 'plant' | 'type' | 'variety' | null;
  /** True while a save is under way. */
  disabled?: boolean;
  /** The fields' ids start with this, so two sets on a page do not clash. */
  idPrefix: string;
  /** The first field's label, where "Plant" alone would be ambiguous. */
  plantLabel?: string;
  /** A search box above the Plant drop-down, listing the crops, types and varieties it finds. */
  search?: boolean;
  /** False to leave out the Plant drop-down, where the crop is chosen elsewhere. */
  showPlant?: boolean;
  /** A variety Grow saw, offered in the Variety list after "Not sure" (the photo tray); null or absent for none. */
  seen?: SeenVarietyOption | null;
}

/** Plant, then Type and Variety when they have a choice to offer. */
export const PlantIdentityFields: React.FC<PlantIdentityFieldsProps> = ({
  value, onChange, missing = null, disabled, idPrefix, plantLabel, search = false, showPlant = true, seen = null,
}) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  /*
   * The server's three answers: the Plant list (only where it is drawn), the chosen crop's types and the chosen type's
   * varieties. "What you grow" (the crops already in the garden, listed first so the usual ones are a tap away, each in
   * its own group below as well) is the answer's `mine` group; an empty garden has none.
   */
  const crops = usePickerCrops(showPlant);
  const cropTypes = useCropTypes(value.cropId);
  const typeVarieties = useTypeVarieties(value.plantId);
  const types = cropTypes.data && cropTypes.data.crop.id === value.cropId ? cropTypes.data : undefined;
  const varieties: PickerVarieties | undefined = typeVarieties.data && typeVarieties.data.plant.id === value.plantId ? typeVarieties.data : undefined;
  /** Picks made in these lists start where the server says, once its answer is in. */
  const starts = usePickStarts(value, onChange);
  const cropsLoading = showPlant && crops.isLoading;

  /*
   * Search lists what it finds under the box, and narrows the drop-down too:
   * typing "tom" leaves Tomato there. The server says both (B121): the rows,
   * and the crops whose names answer (cropIds). The crop already chosen
   * always stays, so the field never loses its value; until the first
   * answer is in, the drop-down lists every crop.
   */
  const [query, setQuery] = React.useState('');
  const q = query.trim();
  const answer = usePlantSearch(query, null, search);
  /** The crops the server says answer what is typed; null while nothing is typed or no answer is in yet. */
  const answering = React.useMemo(
    () => (q !== '' && answer.data ? new Set(answer.data.cropIds) : null),
    [q, answer.data]
  );
  /** Whether a crop stays in the list: it answers the search, or it is the one chosen. */
  const matches = React.useCallback((c: PickerCrop): boolean => (
    answering === null || c.id === value.cropId || answering.has(c.id)
  ), [answering, value.cropId]);
  /** The Plant drop-down's groups as the server sent them, narrowed to the crops that answer what is typed. */
  const groups = React.useMemo(
    () => (crops.data?.groups ?? []).map((group) => ({ kind: group.kind, crops: group.crops.filter(matches) })).filter((group) => group.crops.length > 0),
    [crops.data, matches]
  );
  /** What the search lists under its box: the server's rows, as sent. */
  const hits = React.useMemo(() => (search ? answer.data?.rows ?? [] : []), [search, answer.data]);
  const showType = types !== undefined && types.types.length > 1;
  /** The type's hidden "Not sure" variety (what "Not sure" saves), or null. */
  const placeholder = varieties?.notSure ?? null;
  const named = varieties?.varieties ?? [];
  // A variety Grow saw is a choice to offer too, beside "Not sure".
  const showVariety = value.plantId !== null && (named.length > 0 || seen !== null);

  /*
   * Naming a variety of their own: the box is open while they type, and the
   * variety made (or found) is added to the list everyone's fields read, and
   * chosen.
   */
  const queryClient = useQueryClient();
  const scope = usePickerScope();
  const [adding, setAdding] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  /** Why naming a variety of their own failed, said under its box; null when it has not. */
  const [addFailed, setAddFailed] = React.useState<string | null>(null);
  /** Closes the box and forgets what was typed in it; it only sets state, so the effects below can call it. */
  const closeAdding = React.useCallback((): void => { setAdding(false); setNewName(''); setAddFailed(null); }, []);
  /*
   * A different plant, or a different type, closes the box: what was typed
   * was for the other one. A type chosen while the box waits for one (it
   * was opened from the search with only the plant chosen) keeps it open.
   */
  const lastType = React.useRef(value.plantId);
  React.useEffect(() => {
    const was = lastType.current;
    lastType.current = value.plantId;
    if (was !== null && was !== value.plantId) closeAdding();
  }, [value.plantId, closeAdding]);
  React.useEffect(() => { closeAdding(); }, [value.cropId, closeAdding]);
  /** Saves the typed name as a variety of the chosen type, and chooses it. */
  const saveOwn = async (): Promise<void> => {
    const name = newName.trim();
    if (!name || value.plantId === null) return;
    setSaving(true);
    setAddFailed(null);
    try {
      const variety = await addOwnVariety(value.plantId, name);
      // In the list at once, so it can be chosen; the server's answer (in its order) is read again behind it.
      queryClient.setQueryData<PickerVarieties>(pickerKeys.varieties(scope, value.plantId), (held) => (
        held && !held.varieties.some((v) => v.id === variety.id)
          ? { ...held, varieties: [...held.varieties, { id: variety.id, name: variety.name, own: true }] }
          : held
      ));
      void pickerStale(queryClient);
      // The search's answers no longer hold every variety: the next search asks again.
      void queryClient.invalidateQueries({ queryKey: [PLANT_SEARCH_QUERY_KEY], refetchType: 'none' });
      onChange({ ...value, varietyId: variety.id });
      setAdding(false);
      setNewName('');
    } catch (err) {
      // The server's refusal, or the standard sentence for a failure on our side (D31).
      setAddFailed(errorMessage(err, t('plants.form.fields.addVarietyFailed')));
    } finally {
      setSaving(false);
    }
  };

  /** A search result chosen: Plant, Type and Variety set at once, and the box emptied. */
  const chooseHit = (key: string): void => {
    const hit = hits.find((each) => each.key === key);
    if (!hit || hit.cropId === null) return;
    // A crop row starts Type as picking the crop does (B65); a type or variety row sets exactly what the server named.
    onChange(hit.kind === 'crop' ? starts.cropPicked(hit.cropId, value) :{ cropId: hit.cropId, plantId: hit.plantId, varietyId: hit.varietyId });
    setQuery('');
  };
  /** Opens the box to name a variety of their own, with what was searched for in it. */
  const addSearchedAsOwn = (): void => {
    setNewName(query.trim().slice(0, limits.VARIETY_NAME_MAX));
    setAddFailed(null);
    setAdding(true);
    setQuery('');
  };

  /** A crop chosen: Type and Variety start where the server says (B65, B52); a type already chosen for it stays. */
  const chooseCrop = (raw: string): void => onChange(starts.cropPicked(raw ? Number(raw) : null, value));
  /** A type chosen: Variety goes to where the server says it starts (its "Not sure", or its only variety). */
  const chooseType = (raw: string): void => {
    // "Not sure" (B52): the crop and the flag, no type and no variety; the server decides the type.
    if (raw === TYPE_UNSURE) {
      onChange({ cropId: value.cropId, plantId: null, varietyId: null, typeUnsure: true });
      return;
    }
    onChange(starts.typePicked(raw ? Number(raw) : null, value));
  };
  /**
   * A variety chosen, or "Add your own variety…", which opens the box and
   * leaves the choice as it was. The variety Grow saw is chosen with the
   * type's "Not sure" variety under it, as it is filed by name; anything
   * else lets it go.
   */
  const chooseVariety = (raw: string): void => {
    if (raw === ADD_OWN) { setAdding(true); return; }
    if (raw === SEEN) {
      seen?.onChoose(true);
      onChange({ ...value, varietyId: varieties?.start.varietyId ?? null });
      return;
    }
    seen?.onChoose(false);
    onChange({ ...value, varietyId: raw ? Number(raw) : null });
  };

  /** A field's "still needs an answer" line, when it is the one missing. */
  const error = (field: 'plant' | 'type' | 'variety'): React.ReactNode => missing === field && (
    <p id={`${idPrefix}-${field}-err`} className="mt-1 text-sm font-medium text-destructive">
      {t(field === 'type' && !types?.notSure ? 'plants.form.missing.typeOnly' : `plants.form.missing.${field}`)}
    </p>
  );

  return (
    <>
      {showPlant && (
        <div>
          <Label className="block" htmlFor={`${idPrefix}-plant`}>{plantLabel ?? t('plants.form.fields.plant')}</Label>
          {search && (
            <PlantSearchField
              idPrefix={idPrefix}
              query={query}
              onQueryChange={setQuery}
              options={hits.map((hit) => ({
                key: hit.key,
                label: hit.own ? `${hit.label} ${t('plants.form.fields.yours')}` : hit.label,
                detail: hit.detail,
              }))}
              onChoose={chooseHit}
              placeholder={t('plants.form.fields.searchPlants')}
              disabled={disabled || cropsLoading}
              // "Nothing found" only once the server has answered what is in the box; its failure says so instead.
              empty={answer.error ? <ServerFailureNote error={answer.error} where="plantSearch" /> : answer.current && (
                <NoPlantFound query={query}>
                  {value.cropId !== null && (
                    <button
                      type="button"
                      onClick={addSearchedAsOwn}
                      disabled={disabled}
                      className="flex min-h-11 items-center text-left text-sm font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {t('plants.form.fields.addAsOwn', { query: query.trim() })}
                    </button>
                  )}
                  {/* A plant Grow does not list: Grow says what it is and where it goes, filed with one Yes. */}
                  <DescribePlant
                    query={query}
                    idPrefix={idPrefix}
                    disabled={disabled}
                    onChosen={(choice) => { onChange(choice); setQuery(''); }}
                  />
                </NoPlantFound>
              )}
            />
          )}
          <NativeSelect
            id={`${idPrefix}-plant`}
            className="mt-1.5"
            value={value.cropId ?? ''}
            onChange={(event) => chooseCrop(event.target.value)}
            disabled={disabled || cropsLoading}
            invalid={missing === 'plant'}
            aria-describedby={missing === 'plant' ? `${idPrefix}-plant-err` : undefined}
          >
            <option value="">{cropsLoading ? t('plants.form.loadingCatalogue') : t('plants.form.fields.choosePlant')}</option>
            {/* The server's groups in its order: "What you grow" (mine) first, then each kind. */}
            {groups.map((group) => (
              <optgroup key={group.kind} label={t(`plants.form.kinds.${group.kind}`)}>
                {group.crops.map((c) => <option key={`${group.kind}-${c.id}`} value={c.id}>{c.name}</option>)}
              </optgroup>
            ))}
          </NativeSelect>
          {error('plant')}
        </div>
      )}

      {(showType || showVariety) && (
        <div className={cn('grid gap-3', showType && showVariety ? 'grid-cols-2' : 'grid-cols-1')}>
          {showType && types && (
            <div className="min-w-0">
              <Label className="block" htmlFor={`${idPrefix}-type`}>{t('plants.form.fields.type')}</Label>
              <NativeSelect
                id={`${idPrefix}-type`}
                className="mt-1.5"
                value={value.typeUnsure ? TYPE_UNSURE : value.plantId ?? ''}
                onChange={(event) => chooseType(event.target.value)}
                disabled={disabled}
                invalid={missing === 'type'}
                aria-describedby={missing === 'type' ? `${idPrefix}-type-err` : undefined}
              >
                <option value="">{t('plants.form.fields.chooseType')}</option>
                {/* "Not sure" for the type (B52): first, as Variety's is; the server files the crop's usual type. */}
                {types.notSure && <option value={TYPE_UNSURE}>{t('plants.form.fields.notSure')}</option>}
                {/* The server's labels in its order; a type of the gardener's own (a plant Grow did not have, from a photo) is marked as their own varieties are. */}
                {types.types.map((type) => (
                  <option key={type.id} value={type.id}>{type.own ? `${type.label} ${t('plants.form.fields.yours')}` : type.label}</option>
                ))}
              </NativeSelect>
              {error('type')}
            </div>
          )}
          {showVariety && (
            <div className="min-w-0">
              <Label className="block" htmlFor={`${idPrefix}-variety`}>
                {t('plants.form.fields.variety')}
              </Label>
              <NativeSelect
                id={`${idPrefix}-variety`}
                className="mt-1.5"
                value={seen?.chosen ? SEEN : value.varietyId ?? ''}
                onChange={(event) => chooseVariety(event.target.value)}
                disabled={disabled}
                invalid={missing === 'variety'}
                aria-describedby={missing === 'variety' ? `${idPrefix}-variety-err` : undefined}
              >
                {/* "Not sure" files no variety; a type with no named ones has nothing else to choose, so it is "Not sure" too. */}
                {placeholder !== null
                  ? <option value={placeholder}>{t('plants.form.fields.notSure')}</option>
                  : <option value="">{named.length === 0 ? t('plants.form.fields.notSure') : t('plants.form.fields.chooseVariety')}</option>}
                {seen && <option value={SEEN}>{seen.label}</option>}
                {named.map((variety) => (
                  <option key={variety.id} value={variety.id}>
                    {variety.own ? `${variety.name} ${t('plants.form.fields.yours')}` : variety.name}
                  </option>
                ))}
                <option value={ADD_OWN}>{t('plants.form.fields.addVariety')}</option>
              </NativeSelect>
              {error('variety')}
            </div>
          )}
        </div>
      )}

      {/* A type with no named varieties: a quiet way to name one, where the Variety list would be. */}
      {value.plantId !== null && !showVariety && !adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={disabled}
          className="-mt-1 self-start text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {t('plants.form.fields.addVarietyShort')}
        </button>
      )}
      {adding && value.cropId !== null && (
        <div>
          <Label className="block" htmlFor={`${idPrefix}-new-variety`}>{t('plants.form.fields.newVariety')}</Label>
          {value.plantId === null && (
            <p className="mt-0.5 text-sm text-muted-foreground">{t('plants.form.fields.addVarietyNeedsType')}</p>
          )}
          <div className="mt-1.5 flex gap-2">
            <Input
              id={`${idPrefix}-new-variety`}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                // Enter adds it rather than sending the whole form.
                if (event.key === 'Enter') { event.preventDefault(); void saveOwn(); }
                if (event.key === 'Escape') { event.preventDefault(); setAdding(false); }
              }}
              placeholder={t('plants.form.fields.newVarietyPlaceholder')}
              maxLength={limits.VARIETY_NAME_MAX}
              autoFocus
              autoComplete="off"
              disabled={disabled || saving}
            />
            <Button type="button" onClick={() => void saveOwn()} disabled={disabled || saving || !newName.trim() || value.plantId === null}>
              {t('plants.form.fields.addIt')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)} disabled={saving}>
              {t('plants.form.fields.cancelAdd')}
            </Button>
          </div>
          {addFailed && <p className="mt-1 text-sm font-medium text-destructive" role="alert"><FailureText text={addFailed} /></p>}
        </div>
      )}
    </>
  );
};
